// © Microsoft Corporation. All rights reserved.

import { Base64 } from "js-base64";
import { SapphireBridgeCallBackFunction } from "./SapphireTypes";

declare global {
    interface Window {
        webkit?: any,
        sapphireWebViewBridge?: any
    }
}

const gJobListeners = {};
const gObservers = {};

function uuid(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function onDataReady(jobId: string, result: string) {
    const decoded = Base64.decode(result);

    if (gJobListeners[jobId] && gJobListeners[jobId].length > 0) {
        gJobListeners[jobId].forEach((callback: SapphireBridgeCallBackFunction) => {
            callback(decoded);
        });

        gJobListeners[jobId] = undefined;
    }

    if (gObservers[jobId] && gObservers[jobId].length > 0) {
        gObservers[jobId].forEach((callback: SapphireBridgeCallBackFunction) => {
            callback(decoded);
        });
    }
}

function subscribeCallback(jobId: string, callback: SapphireBridgeCallBackFunction) {
    if (gJobListeners[jobId]) {
        gJobListeners[jobId].push(callback);
    } else {
        gJobListeners[jobId] = [callback];
    }
}

function subscribeObserver(jobId: string, callback: SapphireBridgeCallBackFunction) {
    if (gObservers[jobId]) {
        gObservers[jobId].push(callback);
    } else {
        gObservers[jobId] = [callback];
    }
}

function unSubscribeObserver(jobId: string) {
    gObservers[jobId] = undefined;
}

/**
 * Determine if sapphire bridge exists or not.
 */
function isSapphire() {
    // TODO: use optional chaining after moved to typescript 3.7
    // const isAndroid = window.sapphireWebViewBridge?.send;
    // const isIOS = window.webkit?.messageHandlers?.send?.postMessage;
    const isAndroid = window.sapphireWebViewBridge && window.sapphireWebViewBridge.send;
    const isIOS = window.webkit
        && window.webkit.messageHandlers
        && window.webkit.messageHandlers.send
        && window.webkit.messageHandlers.send.postMessage;
    return isAndroid || isIOS;
}

let gInited = false;

/**
 * Initialize sapphire bridge.
 * You have to invoke this method before any bridge call.
 */
function initSapphire() {
    if (!gInited && isSapphire()) {
        if (!window.sapphireWebViewBridge) {  // iOS will not have sapphireWebViewBridge object
            window.sapphireWebViewBridge = {};
        }
        window.sapphireWebViewBridge.$onDataReady = onDataReady;
        gInited = true;
    }
}

let gAppId = undefined;
/**
 * Set default app id for this bridge instance.
 * In future, every bridge call should has a valid app id, otherwise this call will be dropped.
 * @param appId
 */
function setDefaultAppId(appId: string) {
    gAppId = appId;
}

/**
 * Post job data to super app.
 * @param scenario scenario name.
 * @param data parameters for this scenario.
 * @returns job id.
 */
function postJobDataToWebView(scenario: string, data: any): string {
    console.log(scenario, data);
    const jobId = uuid();
    const msgObj = {
        scenario: scenario,
        data: {
            data: data
        },
        id: jobId
    };
    if (gAppId && !msgObj.data.data["appId"]) {
        msgObj.data.data["appId"] = gAppId;
    }

    const jsonMsg = JSON.stringify(msgObj);
    // TODO: use optional chaining after moved to typescript 3.7
    // window.webkit?.messageHandlers?.send?.postMessage(jsonMsg);
    // window.sapphireWebViewBridge?.send(jsonMsg);
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.send) {
        window.webkit.messageHandlers.send.postMessage(jsonMsg);
    }
    if (window.sapphireWebViewBridge && window.sapphireWebViewBridge.send) {
        window.sapphireWebViewBridge.send(jsonMsg);
    }
    return jobId;
}

function invoke(method: string, data: any, callback?: SapphireBridgeCallBackFunction) {
    const jobId = postJobDataToWebView(method, data);
    if (callback) {
        subscribeCallback(jobId, callback);
    }
}

function listen(data: any, handler: SapphireBridgeCallBackFunction): string {
    const handlerId = postJobDataToWebView("subscribe", data);
    subscribeObserver(handlerId, handler);
    return handlerId;
}

function unlisten(handlerId: string, data: any, callback?: SapphireBridgeCallBackFunction) {
    invoke("unSubscribe", data, (result) => {
        callback && callback(result);
    });
    unSubscribeObserver(handlerId);
}

export {
    isSapphire,
    initSapphire,
    setDefaultAppId,
    invoke,
    listen,
    unlisten
};
